import * as React from "react";

import { useReducedMotion } from "@/lib/motion";
import { cn } from "@/lib/utils";
import { colors, spacing, motion } from "@/ui/tokens";

export interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  animate?: boolean;
}

export function Skeleton({ className, animate = true, ...props }: SkeletonProps) {
  const reducedMotion = useReducedMotion();

  return (
    <div
      className={cn(
        "rounded-md",
        colors.surfaces.skeleton,
        animate && !reducedMotion ? motion.animations.shimmer : null,  // CANON v1.3.0: shimmer-first (2s loop)
        className
      )}
      {...props}
    />
  );
}

export interface SkeletonLineProps extends SkeletonProps {
  width?: string | number;
  height?: string | number;
}

export function SkeletonLine({
  className,
  width = "100%",
  height = "1rem",
  animate,
  style,
  ...props
}: SkeletonLineProps) {
  return (
    <Skeleton
      className={className}
      style={{ width, height, ...(style ?? {}) }}
      animate={animate}
      {...props}
    />
  );
}

export interface SkeletonAvatarProps extends SkeletonProps {
  size?: "sm" | "md" | "lg";
}

const avatarSize = {
  sm: "h-8 w-8",
  md: "h-10 w-10",
  lg: "h-12 w-12",
};

export function SkeletonAvatar({ className, size = "md", animate, ...props }: SkeletonAvatarProps) {
  return (
    <Skeleton
      className={cn("rounded-full", avatarSize[size], className)}
      animate={animate}
      {...props}
    />
  );
}

export interface SkeletonCardProps extends SkeletonProps {
  showAvatar?: boolean;
  lines?: number;
}

export function SkeletonCard({
  className,
  showAvatar = false,
  lines = 3,
  animate,
  ...props
}: SkeletonCardProps) {
  return (
    <div
      className={cn(
        "rounded-lg",
        colors.surfaces.card,
        spacing.inset.md,
        motion.transitions.layout,
        className
      )}
      data-testid="skeleton-card"
      {...props}
    >
      {showAvatar && (
        <div className={cn("flex items-center", spacing.inline.sm)}>
          <SkeletonAvatar animate={animate} size="md" />
          <SkeletonLine animate={animate} width="60%" height="1rem" />
        </div>
      )}

      <div className={cn(spacing.stack.xs, "mt-3")}
      >
        {Array.from({ length: lines }, (_, index) => (
          <SkeletonLine
            key={index}
            animate={animate}
            width={index === lines - 1 ? "75%" : "100%"}
            height="0.875rem"
          />
        ))}
      </div>
    </div>
  );
}
